interface Lead {
  created_at: string;
}

interface WeeklyTrendChartProps {
  leads: Lead[];
}

const DAY_LABELS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

export default function WeeklyTrendChart({ leads }: WeeklyTrendChartProps) {
  const today = new Date();

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (6 - i));
    return d;
  });

  const counts = days.map((day) =>
    leads.filter((l) => {
      const d = new Date(l.created_at);
      return d.toDateString() === day.toDateString();
    }).length
  );

  const maxCount = Math.max(...counts, 1);
  const totalWeek = counts.reduce((a, b) => a + b, 0);
  const avgPerDay = (totalWeek / 7).toFixed(1);

  // SVG polyline points
  const chartW = 500;
  const chartH = 100;
  const padX = 20;
  const padY = 10;
  const innerW = chartW - padX * 2;
  const innerH = chartH - padY * 2;

  const points = counts.map((c, i) => {
    const x = padX + (i / 6) * innerW;
    const y = padY + innerH - (c / maxCount) * innerH;
    return { x, y, count: c };
  });

  const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(' ');
  const areaPoints = [
    `${points[0].x},${chartH}`,
    ...points.map((p) => `${p.x},${p.y}`),
    `${points[points.length - 1].x},${chartH}`,
  ].join(' ');

  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-bold text-sm">Haftalık Lead Trendi</h3>
          <p className="text-gray-300 text-xs mt-0.5">Son 7 gün</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-white font-black text-xl">{totalWeek}</p>
            <p className="text-gray-300 text-xs">Bu hafta</p>
          </div>
          <div className="text-right">
            <p className="text-white font-black text-xl">{avgPerDay}</p>
            <p className="text-gray-300 text-xs">Günlük ort.</p>
          </div>
        </div>
      </div>

      {/* SVG Chart */}
      <div className="relative w-full overflow-hidden">
        <svg
          viewBox={`0 0 ${chartW} ${chartH}`}
          className="w-full"
          style={{ height: '100px' }}
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f0b429" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#f0b429" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Grid lines */}
          {[0.25, 0.5, 0.75, 1].map((ratio) => (
            <line
              key={ratio}
              x1={padX}
              y1={padY + innerH * (1 - ratio)}
              x2={chartW - padX}
              y2={padY + innerH * (1 - ratio)}
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="1"
            />
          ))}
          {/* Area fill */}
          <polygon points={areaPoints} fill="url(#areaGrad)" />
          {/* Line */}
          <polyline
            points={polylinePoints}
            fill="none"
            stroke="#f0b429"
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {/* Dots */}
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="5" fill="#0f0f1a" stroke="#f0b429" strokeWidth="2" />
              {p.count > 0 && (
                <circle cx={p.x} cy={p.y} r="3" fill="#f0b429" />
              )}
            </g>
          ))}
        </svg>
      </div>

      {/* Day labels */}
      <div className="flex justify-between mt-2 px-1">
        {days.map((day, i) => (
          <div key={i} className="flex flex-col items-center gap-1">
            <span
              className="text-xs font-semibold"
              style={{ color: day.toDateString() === today.toDateString() ? '#f0b429' : '#d1d5db' }}
            >
              {DAY_LABELS[day.getDay() === 0 ? 6 : day.getDay() - 1]}
            </span>
            <span
              className="text-xs font-black"
              style={{ color: counts[i] > 0 ? '#fff' : '#9ca3af' }}
            >
              {counts[i]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
